import { useMemo } from 'react';
import { CffAsyncTypeahead, type CffAsyncOption } from './CffAsyncTypeahead';
import {
  pufCustomerOptions,
  pufItemOptions,
  searchPufCustomers,
  searchPufItems,
  type PufCustomerLookup,
  type PufItemLookup,
} from '../../lib/pricingUpdateLookup';
import { splitPufItemCodeAndDescription } from '../../lib/pricingUpdateSubmissions';
import {
  createPricingUpdateItemRow,
  type PricingUpdateAttachmentRef,
  type PricingUpdateFormData,
  type PricingUpdateItemRow,
} from '../../types/applicationForm';

export type PricingUpdateFormMode = 'new' | 'detail';

type PricingUpdateFormProps = {
  value: PricingUpdateFormData;
  onChange: (next: PricingUpdateFormData) => void;
  mode: PricingUpdateFormMode;
  /** Requestor fields editable (New page). */
  requestorEditable: boolean;
  /** Authorised price / item columns editable. */
  authorisedEditable: boolean;
  /** Reason of the decision editable (Authorised edit, or Approver approve/return). */
  decisionReasonEditable?: boolean;
  /** Show required mark — only for Approve / Reject / Return (not Save). */
  decisionReasonRequired?: boolean;
  showAdminSection: boolean;
  pendingRequestorFiles?: File[];
  onPendingRequestorFilesChange?: (files: File[]) => void;
  pendingAuthorisedFiles?: File[];
  onPendingAuthorisedFilesChange?: (files: File[]) => void;
  onOpenAttachment?: (ref: PricingUpdateAttachmentRef) => void;
  onRemoveSavedAttachment?: (kind: 'requestor' | 'authorised', path: string) => void;
};

function patchItem(
  items: PricingUpdateItemRow[],
  id: string,
  patch: Partial<PricingUpdateItemRow>,
): PricingUpdateItemRow[] {
  return items.map((row) => (row.id === id ? { ...row, ...patch } : row));
}

function readonlyClass(disabled: boolean): string {
  return disabled ? 'field-input field-input--readonly' : 'field-input';
}

function AttachmentList({
  label,
  saved,
  pending,
  disabled,
  onPick,
  onOpen,
  onRemoveSaved,
  onRemovePending,
}: {
  label: string;
  saved: PricingUpdateAttachmentRef[];
  pending: File[];
  disabled: boolean;
  onPick: (files: FileList | null) => void;
  onOpen?: (ref: PricingUpdateAttachmentRef) => void;
  onRemoveSaved?: (path: string) => void;
  onRemovePending?: (index: number) => void;
}) {
  return (
    <div className="puf-form__attachments">
      <div className="field-label">{label}</div>
      <p className="field-hint">Any format · multiple files · max 20MB each</p>
      {!disabled ? (
        <label className="btn-secondary btn-sm puf-form__upload-btn">
          Add files
          <input
            type="file"
            multiple
            hidden
            onChange={(e) => {
              onPick(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      ) : null}
      <ul className="puf-form__file-list">
        {saved.map((ref) => (
          <li key={ref.path}>
            {onOpen ? (
              <button type="button" className="puf-form__file-link" onClick={() => onOpen(ref)}>
                {ref.fileName}
              </button>
            ) : (
              <span>{ref.fileName}</span>
            )}
            {!disabled && onRemoveSaved ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => onRemoveSaved(ref.path)}
              >
                Remove
              </button>
            ) : null}
          </li>
        ))}
        {pending.map((file, index) => (
          <li key={`pending-${file.name}-${index}`}>
            <span>{file.name} (pending upload)</span>
            {!disabled && onRemovePending ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => onRemovePending(index)}
              >
                Remove
              </button>
            ) : null}
          </li>
        ))}
        {saved.length === 0 && pending.length === 0 ? (
          <li className="puf-form__file-empty">No attachments</li>
        ) : null}
      </ul>
    </div>
  );
}

export function PricingUpdateForm({
  value,
  onChange,
  mode,
  requestorEditable,
  authorisedEditable,
  decisionReasonEditable,
  decisionReasonRequired = false,
  showAdminSection,
  pendingRequestorFiles = [],
  onPendingRequestorFilesChange,
  pendingAuthorisedFiles = [],
  onPendingAuthorisedFilesChange,
  onOpenAttachment,
  onRemoveSavedAttachment,
}: PricingUpdateFormProps) {
  const showAuthorisedColumns = showAdminSection;
  const reasonEditable = decisionReasonEditable ?? authorisedEditable;
  /** Requestor (new) or Authorised (submitted/processing) may add/remove item rows. */
  const itemsMutable = requestorEditable || authorisedEditable;

  const searchCustomers = useMemo(
    () => async (query: string) => pufCustomerOptions(await searchPufCustomers(query)),
    [],
  );
  const searchItems = useMemo(
    () => async (query: string) => pufItemOptions(await searchPufItems(query)),
    [],
  );

  const setField = <K extends keyof PricingUpdateFormData>(key: K, next: PricingUpdateFormData[K]) => {
    onChange({ ...value, [key]: next });
  };

  const onCustomerSelect = (option: CffAsyncOption) => {
    const meta = option.meta as PufCustomerLookup | undefined;
    onChange({
      ...value,
      customer_no: option.value,
      customer_name: String(meta?.name ?? '').trim(),
      customer_price_group: String(meta?.customer_price_group ?? '').trim(),
    });
  };

  const onItemSelect = (rowId: string, option: CffAsyncOption) => {
    const meta = option.meta as PufItemLookup | undefined;
    const split = splitPufItemCodeAndDescription(
      option.value,
      String(meta?.description ?? '').trim(),
    );
    setField(
      'items',
      patchItem(value.items, rowId, {
        item_no: split.item_no,
        item_description: split.item_description,
        sales_uom: String(meta?.sales_uom ?? '').trim(),
      }),
    );
  };

  const onItemCodeChange = (rowId: string, next: string) => {
    const split = splitPufItemCodeAndDescription(next, '');
    const combinedLabel = split.item_no !== next.trim();
    setField(
      'items',
      patchItem(value.items, rowId, {
        item_no: split.item_no,
        // Typing a bare code clears description; pasted/typed "CODE — Desc" keeps desc.
        item_description: combinedLabel ? split.item_description : '',
        sales_uom: '',
      }),
    );
  };

  return (
    <div className={`puf-form puf-form--cards${mode === 'new' ? ' puf-form--new' : ''}`}>
      <section className="card puf-form-card">
        <h3 className="puf-form__section-title">Customer Information</h3>
        <div className="puf-form__grid puf-form__grid--customer">
          <label className="field puf-form__field--cust-no">
            <span className="field-label">
              Customer no. <span className="af-form__req">*</span>
            </span>
            {requestorEditable ? (
              <CffAsyncTypeahead
                value={value.customer_no}
                onChange={(next) =>
                  onChange({
                    ...value,
                    customer_no: next,
                    customer_name: '',
                    customer_price_group: '',
                  })
                }
                onSelect={onCustomerSelect}
                search={searchCustomers}
                resolveExactOnBlur
                placeholder="Customer no. / name"
                ariaLabel="Customer number"
              />
            ) : (
              <input
                className={readonlyClass(true)}
                value={value.customer_no}
                disabled
                readOnly
              />
            )}
          </label>
          <label className="field puf-form__field--cust-name">
            <span className="field-label">Customer name</span>
            <input
              className={readonlyClass(true)}
              value={value.customer_name}
              disabled
              readOnly
            />
          </label>
          <label className="field puf-form__field--price-group">
            <span className="field-label">Customer price group</span>
            <input
              className={readonlyClass(true)}
              value={value.customer_price_group}
              disabled
              readOnly
            />
          </label>
        </div>
      </section>

      <section className="card puf-form-card">
        <div className="puf-form__items-head">
          <h3 className="puf-form__section-title puf-form__section-title--inline">
            Item Information
          </h3>
          {itemsMutable ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => {
                // Always add a blank pickable row so Authorised can add different items
                // after submit (not only MOQ tiers of the previous line).
                setField('items', [...value.items, createPricingUpdateItemRow()]);
              }}
            >
              + Add item
            </button>
          ) : null}
        </div>

        {showAuthorisedColumns ? (
          <div className="puf-form__table-scroll">
            <table className="puf-form__table puf-form__table--stacked puf-form__table--authorised">
              <thead>
                <tr>
                  <th className="puf-form__col-item">Item</th>
                  <th className="puf-form__col-uom">UOM</th>
                  <th className="puf-form__col-price">Price</th>
                  <th className="puf-form__col-moq">MOQ</th>
                  <th className="puf-form__col-date">Effective</th>
                  <th className="puf-form__col-date">Ending</th>
                  {itemsMutable ? (
                    <th className="puf-form__col-actions">
                      <span className="puf-form__sr-only">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {value.items.map((row) => {
                  const itemSplit = splitPufItemCodeAndDescription(
                    row.item_no,
                    row.item_description,
                  );
                  // Stay in typeahead while typing. Lock only after a real pick
                  // (list select / exact blur match sets description and/or UOM).
                  const itemConfirmed =
                    Boolean(itemSplit.item_no.trim()) &&
                    (Boolean(itemSplit.item_description.trim()) ||
                      Boolean(row.sales_uom.trim()));
                  const itemPickable = itemsMutable && !itemConfirmed;
                  return (
                    <tr key={row.id}>
                      <td className="puf-form__col-item">
                        {itemPickable ? (
                          <div className="puf-form__item-stack">
                            <CffAsyncTypeahead
                              value={itemSplit.item_no}
                              onChange={(next) => onItemCodeChange(row.id, next)}
                              onSelect={(option) => onItemSelect(row.id, option)}
                              search={searchItems}
                              resolveExactOnBlur
                              placeholder="Item no."
                              ariaLabel="Item number for row"
                            />
                            <span
                              className={`puf-form__item-desc${
                                itemSplit.item_description ? '' : ' is-muted'
                              }`}
                            >
                              {itemSplit.item_description ||
                                'Pick from list, or type full item no.'}
                            </span>
                          </div>
                        ) : (
                          <div className="puf-form__item-stack">
                            <span className="puf-form__item-code" title={itemSplit.item_no}>
                              {itemSplit.item_no || '—'}
                            </span>
                            {itemSplit.item_description ? (
                              <span
                                className="puf-form__item-desc"
                                title={itemSplit.item_description}
                              >
                                {itemSplit.item_description}
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="puf-form__col-uom">
                        <span className="puf-form__side-cell">{row.sales_uom.trim() || '—'}</span>
                      </td>
                      <td className="puf-form__col-price">
                        <input
                          className={readonlyClass(!authorisedEditable)}
                          value={row.sales_price}
                          disabled={!authorisedEditable}
                          readOnly={!authorisedEditable}
                          onChange={(e) =>
                            setField(
                              'items',
                              patchItem(value.items, row.id, { sales_price: e.target.value }),
                            )
                          }
                        />
                      </td>
                      <td className="puf-form__col-moq">
                        <input
                          className={readonlyClass(!authorisedEditable)}
                          value={row.moq}
                          disabled={!authorisedEditable}
                          readOnly={!authorisedEditable}
                          onChange={(e) =>
                            setField('items', patchItem(value.items, row.id, { moq: e.target.value }))
                          }
                        />
                      </td>
                      <td className="puf-form__col-date">
                        <input
                          type="date"
                          className={`${readonlyClass(!authorisedEditable)} puf-form__date-input${
                            row.effective_date.trim() ? '' : ' is-empty'
                          }`}
                          value={row.effective_date}
                          disabled={!authorisedEditable}
                          readOnly={!authorisedEditable}
                          onChange={(e) =>
                            setField(
                              'items',
                              patchItem(value.items, row.id, { effective_date: e.target.value }),
                            )
                          }
                        />
                      </td>
                      <td className="puf-form__col-date">
                        <input
                          type="date"
                          className={`${readonlyClass(!authorisedEditable)} puf-form__date-input${
                            row.ending_date.trim() ? '' : ' is-empty'
                          }`}
                          value={row.ending_date}
                          disabled={!authorisedEditable}
                          readOnly={!authorisedEditable}
                          onChange={(e) =>
                            setField(
                              'items',
                              patchItem(value.items, row.id, { ending_date: e.target.value }),
                            )
                          }
                        />
                      </td>
                      {itemsMutable ? (
                        <td className="puf-form__col-actions">
                          <button
                            type="button"
                            className="btn-secondary btn-sm puf-form__row-remove"
                            disabled={value.items.length <= 1}
                            aria-label="Remove item"
                            title="Remove item"
                            onClick={() =>
                              setField(
                                'items',
                                value.items.length > 1
                                  ? value.items.filter((item) => item.id !== row.id)
                                  : value.items,
                              )
                            }
                          >
                            ×
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="puf-form__item-list">
            <div className="puf-form__item-list-head" aria-hidden>
              <span className="puf-form__item-line puf-form__item-line--with-moq">
                <span>Item</span>
                <span>UOM</span>
                <span>MOQ</span>
                <span />
              </span>
            </div>
            {value.items.map((row) => {
              const itemSplit = splitPufItemCodeAndDescription(
                row.item_no,
                row.item_description,
              );
              return (
                <div key={row.id} className="puf-form__item-row">
                  <div className="puf-form__item-line puf-form__item-line--with-moq">
                    {requestorEditable ? (
                      <CffAsyncTypeahead
                        value={itemSplit.item_no}
                        onChange={(next) => onItemCodeChange(row.id, next)}
                        onSelect={(option) => onItemSelect(row.id, option)}
                        search={searchItems}
                        resolveExactOnBlur
                        placeholder="Item no."
                        ariaLabel="Item number for row"
                      />
                    ) : (
                      <span className="puf-form__item-code" title={itemSplit.item_no}>
                        {itemSplit.item_no || '—'}
                      </span>
                    )}
                    <span className="puf-form__side-cell">
                      {row.sales_uom.trim() || '—'}
                    </span>
                    {requestorEditable ? (
                      <input
                        className="field-input puf-form__moq-input"
                        value={row.moq}
                        inputMode="decimal"
                        placeholder="MOQ"
                        aria-label="MOQ for row"
                        onChange={(e) =>
                          setField(
                            'items',
                            patchItem(value.items, row.id, { moq: e.target.value }),
                          )
                        }
                      />
                    ) : (
                      <span className="puf-form__side-cell">
                        {row.moq.trim() || '—'}
                      </span>
                    )}
                    {requestorEditable ? (
                      <button
                        type="button"
                        className="btn-secondary btn-sm puf-form__row-remove"
                        disabled={value.items.length <= 1}
                        aria-label="Remove item"
                        title="Remove item"
                        onClick={() =>
                          setField(
                            'items',
                            value.items.length > 1
                              ? value.items.filter((item) => item.id !== row.id)
                              : value.items,
                          )
                        }
                      >
                        ×
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                  {requestorEditable ? (
                    <span
                      className={`puf-form__item-desc${
                        itemSplit.item_description ? '' : ' is-muted'
                      }`}
                    >
                      {itemSplit.item_description ||
                        'Pick from list, or type full item no.'}
                    </span>
                  ) : itemSplit.item_description ? (
                    <span className="puf-form__item-desc" title={itemSplit.item_description}>
                      {itemSplit.item_description}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <label className="field field--full puf-form__reason">
          <span className="field-label">
            Reason <span className="af-form__req">*</span>
          </span>
          <textarea
            className={`${readonlyClass(!requestorEditable)} puf-form__textarea`}
            rows={3}
            value={value.reason_requestor}
            disabled={!requestorEditable}
            readOnly={!requestorEditable}
            onChange={(e) => setField('reason_requestor', e.target.value)}
          />
        </label>

        <AttachmentList
          label="Attachments"
          saved={value.requestor_attachments}
          pending={pendingRequestorFiles}
          disabled={!requestorEditable}
          onPick={(files) => {
            if (!files?.length || !onPendingRequestorFilesChange) return;
            onPendingRequestorFilesChange([...pendingRequestorFiles, ...Array.from(files)]);
          }}
          onOpen={onOpenAttachment}
          onRemoveSaved={
            onRemoveSavedAttachment
              ? (path) => onRemoveSavedAttachment('requestor', path)
              : undefined
          }
          onRemovePending={
            onPendingRequestorFilesChange
              ? (index) =>
                  onPendingRequestorFilesChange(
                    pendingRequestorFiles.filter((_, i) => i !== index),
                  )
              : undefined
          }
        />
      </section>

      {showAdminSection ? (
        <section className="card puf-form-card">
          <h3 className="puf-form__section-title">Admin Information</h3>
          <label className="field field--full">
            <span className="field-label">
              Reason of the decision{' '}
              {decisionReasonRequired ? <span className="af-form__req">*</span> : null}
            </span>
            {reasonEditable ? (
              <p className="field-hint">
                Required when you Approve or Reject (not needed to Save item edits).
              </p>
            ) : null}
            <textarea
              className={`${readonlyClass(!reasonEditable)} puf-form__textarea`}
              rows={3}
              value={value.reason_decision}
              disabled={!reasonEditable}
              readOnly={!reasonEditable}
              onChange={(e) => setField('reason_decision', e.target.value)}
            />
          </label>
          <AttachmentList
            label="Attachments"
            saved={value.authorised_attachments}
            pending={pendingAuthorisedFiles}
            disabled={!authorisedEditable}
            onPick={(files) => {
              if (!files?.length || !onPendingAuthorisedFilesChange) return;
              onPendingAuthorisedFilesChange([...pendingAuthorisedFiles, ...Array.from(files)]);
            }}
            onOpen={onOpenAttachment}
            onRemoveSaved={
              onRemoveSavedAttachment
                ? (path) => onRemoveSavedAttachment('authorised', path)
                : undefined
            }
            onRemovePending={
              onPendingAuthorisedFilesChange
                ? (index) =>
                    onPendingAuthorisedFilesChange(
                      pendingAuthorisedFiles.filter((_, i) => i !== index),
                    )
                : undefined
            }
          />
        </section>
      ) : null}
    </div>
  );
}
